import { useEffect, useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import Layout from "@/components/Layout";
import JobTimeline from "@/components/JobTimeline";
import DesignFileViewer from "@/components/DesignFileViewer";
import AdditionalAssetsPanel from "@/components/AdditionalAssetsPanel";
import ApprovalForm from "@/components/ApprovalForm";
import AssetUploader from "@/components/AssetUploader";
import MessageAttachment from "@/components/MessageAttachment";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { CheckCircle, ArrowClockwise, PaperPlaneRight, Link as LinkIcon, CaretDown, CaretUp, X } from "@phosphor-icons/react";

const PHASES = ["Onboarding", "Quote", "Design", "Review", "Final Proof Sign-Off", "Production", "Shipping/Go Live", "Completed"];

export default function JobDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [job, setJob] = useState(null);
  const [selectedIter, setSelectedIter] = useState(null);
  const [msgText, setMsgText] = useState("");
  const [msgAssets, setMsgAssets] = useState([]);
  const [selectedPhase, setSelectedPhase] = useState(null);
  const [expandedProof, setExpandedProof] = useState(null);

  const load = async () => {
    const { data } = await api.get(`/jobs/${id}`);
    setJob(data);
    const designs = data.designs || [];
    // Prefer the latest ready-for-review iteration; else the most recent iteration
    const latestReady = [...designs].reverse().find(d => d.status === "ready_for_review");
    const target = latestReady || designs[designs.length - 1];
    if (target) setSelectedIter(target.id);
    if (!selectedPhase) setSelectedPhase(data.phase);
  };
  useEffect(() => { load(); }, [id]);

  // Auto-mark messages as read whenever the job renders with an unread count.
  useEffect(() => {
    if (job && (job.unread_message_count || 0) > 0) {
      api.post(`/jobs/${id}/messages/read`).catch(() => {});
    }
  }, [id, job?.unread_message_count]);

  const hadRevisions = useMemo(() =>
    !!job && (job.review_history || []).some(h => h.decision === "revisions"),
    [job]
  );
  const hadRejection = useMemo(() =>
    !!job && (job.final_proof_history || []).some(h => h.approval?.status === "not_approved"),
    [job]
  );

  if (!job) return <Layout><div className="p-10 tech-label">LOADING…</div></Layout>;

  const iteration = job.designs?.find((d) => d.id === selectedIter);
  const latestReviewable = job.designs?.filter((d) => d.status === "ready_for_review").slice(-1)[0];
  const activePhase = selectedPhase || job.phase;

  const submitReviewDecision = async (decision, selected_design_id = null) => {
    try {
      await api.post(`/jobs/${id}/review-decision`, { decision, selected_design_id });
      toast.success(decision === "revisions" ? "Revisions requested" : "Design selected — moving to final proof");
      setSelectedPhase(null);  // reset to current on next load
      await load();
    } catch { toast.error("Failed"); }
  };

  const sendMessage = async () => {
    if (!msgText.trim()) return;
    try {
      await api.post(`/jobs/${id}/messages`, { text: msgText, asset_ids: msgAssets.map((a) => a.id) });
      setMsgText(""); setMsgAssets([]);
      await load();
    } catch { toast.error("Failed to send"); }
  };

  // Determine which section to render based on selected phase
  const renderPhase = () => {
    switch (activePhase) {
      case "Onboarding":
        return (
          <section className="pt-6" data-testid="phase-section-onboarding">
            <div className="chip mb-4">// onboarding summary</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <div className="tech-label">Products</div>
                <div className="text-sm font-semibold mt-1">{job.products?.length ? `${job.products.length} selected` : "—"}</div>
              </div>
              <div>
                <div className="tech-label">Assets</div>
                <div className="text-sm font-semibold mt-1">{job.asset_ids?.length || 0} file(s)</div>
              </div>
            </div>
            {job.questionnaire && Object.keys(job.questionnaire).length > 0 && (
              <div className="mt-6">
                <div className="tech-label mb-3">Questionnaire</div>
                <div className="space-y-4">
                  {Object.entries(job.questionnaire).map(([k, v]) => (
                    <div key={k} className="pb-3 border-b border-[var(--gc-border)]">
                      <div className="tech-label">{k.replace(/_/g, ' ')}</div>
                      <div className="text-sm text-[var(--gc-navy)] mt-1">{v || "—"}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </section>
        );

      case "Quote":
        return job.quote ? (
          <section className="pt-6" data-testid="phase-section-quote">
            <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
              <div className="chip chip-orange">// quote {job.quote.status === "accepted" ? "· accepted" : "· pending"}</div>
              {job.quote.status === "accepted" && <CheckCircle size={22} className="text-[var(--gc-success)]" weight="fill"/>}
            </div>
            {job.quote.notes && <p className="text-sm text-[var(--gc-navy)] mb-3">{job.quote.notes}</p>}
            <div className="border border-[var(--gc-border)] bg-white aspect-video">
              <iframe src={job.quote.quote_url} title="Quote" className="w-full h-full" data-testid="quote-iframe" />
            </div>
            <a href={job.quote.quote_url} target="_blank" rel="noreferrer" className="mt-3 inline-flex items-center gap-2 text-sm text-[var(--gc-orange)] font-semibold link-underline" data-testid="quote-open-link">
              <LinkIcon size={14} weight="bold"/> Open quote in new tab
            </a>
          </section>
        ) : (
          <section className="pt-6"><div className="text-sm text-[var(--gc-text-muted)]">Quote hasn&apos;t been posted yet.</div></section>
        );

      case "Design":
      case "Review":
        return job.designs?.length > 0 ? (
          <section className="pt-6" data-testid="phase-section-design">
            <div className="chip mb-4">// design gallery</div>

            {user?.role === "customer" && latestReviewable && (job.phase === "Design" || job.phase === "Review") && (
              <div className="mb-6 border-l-4 border-[var(--gc-orange)] bg-[var(--gc-orange-tint)] p-5" data-testid="review-decision-panel">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div>
                    <div className="chip chip-orange mb-2">// your review · action needed</div>
                    <div className="serif text-2xl md:text-3xl leading-tight">Ready to decide?</div>
                    <p className="text-sm text-[var(--gc-navy)] mt-2 max-w-xl">
                      Review the design{latestReviewable.files?.length > 1 ? "s" : ""} below and either request revisions or approve to move to final proof sign-off.
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button onClick={() => submitReviewDecision("revisions")}
                      className="btn-secondary inline-flex items-center gap-2 px-4 py-2.5 text-sm"
                      data-testid="request-revisions-btn">
                      <ArrowClockwise size={16} weight="bold"/> Request Revisions
                    </button>
                    {latestReviewable.files?.length === 1 ? (
                      <button onClick={() => submitReviewDecision("continue", latestReviewable.files[0].file_id)}
                        className="btn-primary inline-flex items-center gap-2 px-4 py-2.5 text-sm"
                        data-testid="approve-design-btn">
                        <CheckCircle size={16} weight="bold"/> Approve Design
                      </button>
                    ) : (
                      latestReviewable.files?.map((f) => (
                        <button key={f.id} onClick={() => submitReviewDecision("continue", f.file_id)}
                          className="btn-primary inline-flex items-center gap-2 px-4 py-2.5 text-sm"
                          data-testid={`continue-with-${f.file_id}`}>
                          <CheckCircle size={16} weight="bold"/> Approve {f.design_number}
                        </button>
                      ))
                    )}
                  </div>
                </div>
              </div>
            )}

            {user?.role === "customer" && !latestReviewable && !job.approval && job.designs?.some(d => d.status === "in_progress") && (
              <div className="mb-6 border-l-4 border-[var(--gc-warning)] bg-yellow-50 p-4 text-sm text-[var(--gc-navy)]" data-testid="awaiting-admin-hint">
                <strong>Design in progress.</strong> Feel free to leave comments — the designer will mark this ready for your final review shortly.
              </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-6">
              <div className="space-y-2">
                {(user?.role === "admin" ? job.designs : (job.designs || []).filter(d => d.status !== "in_progress")).map((d) => {
                  const statusLabel =
                    d.status === "approved" ? "Approved" :
                    d.status === "revised" ? "Revised" :
                    d.status === "revisions_requested" ? "Revisions Requested" :
                    d.status === "ready_for_review" ? "Ready for review" :
                    d.status === "in_progress" ? "In progress" :
                    d.status.replace(/_/g, ' ');
                  const isLatestReviewable = latestReviewable && d.id === latestReviewable.id;
                  const statusColor =
                    d.status === 'approved' ? 'text-[var(--gc-success)]' :
                    isLatestReviewable ? 'text-[var(--gc-orange)]' :
                    d.status === 'revisions_requested' ? 'text-[var(--gc-warning)]' :
                    d.status === 'revised' ? 'text-[var(--gc-text-muted)]' :
                    d.status === 'in_progress' ? 'text-[var(--gc-navy)]' : '';
                  return (
                    <button key={d.id} onClick={() => setSelectedIter(d.id)}
                      className={`w-full text-left p-3 border-l-4 transition-colors ${selectedIter === d.id ? 'border-[var(--gc-orange)] bg-[var(--gc-orange-tint)]' : 'border-[var(--gc-border)] bg-white hover:bg-[var(--gc-bg-alt)]'}`}
                      data-testid={`iteration-btn-${d.id}`}>
                      <div className="font-mono-tech text-xs text-[var(--gc-text-muted)]">V.{String(d.iteration_number).padStart(2, '0')}</div>
                      <div className="text-sm font-semibold mt-1">{d.files?.length || 0} file(s)</div>
                      <div className={`tech-label mt-1 ${statusColor}`}>{statusLabel}</div>
                    </button>
                  );
                })}
              </div>
              <div className="space-y-6">
                {iteration?.files?.map((f) => (
                  <DesignFileViewer key={f.id} jobId={job.id} job={job} design={iteration} file={f} onCommentAdded={load} />
                ))}
                <AdditionalAssetsPanel
                  jobId={job.id}
                  onAdded={load}
                  title="Upload additional assets for this revision"
                />
              </div>
            </div>
          </section>
        ) : (
          <section className="pt-6">
            <div className="text-sm text-[var(--gc-text-muted)]">No designs uploaded yet.</div>
            <AdditionalAssetsPanel
              jobId={job.id}
              onAdded={load}
              title="Upload additional assets for this revision"
            />
          </section>
        );

      case "Final Proof Sign-Off":
        return job.final_proof ? (
          <section className="pt-6" data-testid="phase-section-final-proof">
            {/* Rejected-proof-awaiting-new banner */}
            {job.approval?.status === "not_approved" && (
              <div className="mb-6 border-l-4 border-[var(--gc-danger)] bg-red-50 p-5" data-testid="rejected-proof-banner">
                <div className="flex items-start gap-3">
                  <X size={22} className="text-[var(--gc-danger)] shrink-0 mt-0.5" weight="bold"/>
                  <div>
                    <div className="serif text-xl text-[var(--gc-danger)]">This is the previously rejected final proof</div>
                    <p className="text-sm text-[var(--gc-navy)] mt-2">
                      You rejected this final proof{job.approval.comments ? <>: <em>&ldquo;{job.approval.comments}&rdquo;</em></> : ''}. Our team has been notified and is working on the revisions.
                    </p>
                    <p className="text-sm text-[var(--gc-navy)] mt-3">
                      <strong>A new final proof will be made available soon</strong> — you&apos;ll receive an email notification the moment it&apos;s ready to review here in the portal.
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="chip chip-orange mb-4">
              {job.approval?.status === "not_approved" ? "// rejected final proof (for reference)" : "// current final proof"}
            </div>
            <DesignFileViewer jobId={job.id} job={job} design={{ id: "final-proof" }} file={{ id: "fp", file_id: job.final_proof.file_id, design_number: job.approval?.status === "not_approved" ? "FINAL PROOF · REJECTED" : "FINAL PROOF" }} readOnly={!!job.approval} onCommentAdded={load} />

            {user?.role === "customer" && !job.approval && (
              <div className="mt-6">
                <ApprovalForm jobId={job.id} onSubmitted={load}
                  customerName={`${user.first_name} ${user.last_name}`}
                  businessName={user.business_name} />
              </div>
            )}
            {job.approval && job.approval.status !== "not_approved" && (
              <div className={`mt-6 border-l-4 p-4 ${job.approval.status === 'approved' ? 'border-[var(--gc-success)] bg-green-50' : 'border-[var(--gc-warning)] bg-yellow-50'}`}>
                <div className="flex items-center gap-2 font-display text-lg">
                  <CheckCircle size={22} weight="fill" className={job.approval.status === 'approved' ? 'text-[var(--gc-success)]' : 'text-[var(--gc-warning)]'}/>
                  <span className="serif text-2xl">Approval submitted — {job.approval.status.replace(/_/g, ' ')}</span>
                </div>
                <div className="text-xs text-[var(--gc-text-muted)] mt-1">Signed by {job.approval.signatory_name} · {new Date(job.approval.signed_at).toLocaleString()}</div>
                {job.approval.comments && <div className="mt-2 text-sm text-[var(--gc-navy)]"><strong>Comments:</strong> {job.approval.comments}</div>}
              </div>
            )}

            {/* Previous final proofs — accordion */}
            {job.final_proof_history?.length > 0 && (
              <div className="mt-10">
                <div className="chip mb-3">// previous final proofs</div>
                <div className="space-y-2">
                  {job.final_proof_history.slice().reverse().map((fp, idx) => {
                    const revNum = job.final_proof_history.length - idx;
                    const isOpen = expandedProof === idx;
                    const status = fp.approval?.status;
                    const statusBadge = status === "not_approved"
                      ? <span className="chip" style={{background:'#fee2e2', color:'#991B1B', borderColor:'#fecaca'}}>Rejected</span>
                      : status === "approved_with_amendments"
                        ? <span className="chip" style={{background:'#fef3c7', color:'#92400E', borderColor:'#fde68a'}}>Amendments</span>
                        : <span className="chip">Superseded</span>;
                    return (
                      <div key={idx} className="border border-[var(--gc-border)]" data-testid={`prev-proof-${idx}`}>
                        <button onClick={() => setExpandedProof(isOpen ? null : idx)}
                          className="w-full flex items-center justify-between p-4 text-left hover:bg-[var(--gc-bg-alt)]"
                          data-testid={`prev-proof-toggle-${idx}`}>
                          <div className="flex items-center gap-3">
                            <span className="tech-label">V.{String(revNum).padStart(2, '0')}</span>
                            <span className="font-semibold text-sm">Final proof — {new Date(fp.uploaded_at).toLocaleDateString()}</span>
                            {statusBadge}
                          </div>
                          {isOpen ? <CaretUp size={18} weight="bold"/> : <CaretDown size={18} weight="bold"/>}
                        </button>
                        {isOpen && (
                          <div className="border-t border-[var(--gc-border)] p-4 bg-[var(--gc-bg-alt)]">
                            <DesignFileViewer jobId={job.id} job={job} design={{ id: `prev-fp-${idx}` }} file={{ id: `pfp-${idx}`, file_id: fp.file_id, design_number: `FINAL PROOF · V.${String(revNum).padStart(2, '0')}` }} readOnly />
                            {fp.approval && (
                              <div className="mt-3 text-sm">
                                <div className="tech-label">Status</div>
                                <div className="font-semibold mt-1">{fp.approval.status.replace(/_/g, ' ')}</div>
                                {fp.approval.comments && <div className="mt-2"><strong>Reason:</strong> {fp.approval.comments}</div>}
                                <div className="text-xs text-[var(--gc-text-muted)] mt-2">Signed by {fp.approval.signatory_name} · {new Date(fp.approval.signed_at).toLocaleString()}</div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </section>
        ) : (
          <section className="pt-6"><div className="text-sm text-[var(--gc-text-muted)]">Final proof hasn&apos;t been posted yet.</div></section>
        );

      case "Production":
        return (
          <section className="pt-6" data-testid="phase-section-production">
            <div className="chip mb-4">// production messages</div>
            <div className="space-y-2 max-h-[400px] overflow-auto">
              {(job.messages || []).map((m) => (
                <div key={m.id} className={`p-3 border-l-4 ${m.author_role === "admin" ? 'border-[var(--gc-orange)] bg-[var(--gc-orange-tint)]' : 'border-[var(--gc-navy)] bg-white'}`} data-testid={`message-${m.id}`}>
                  <div className="flex items-center justify-between mb-1">
                    <div className="text-sm font-semibold">{m.author_name} <span className="tech-label ml-1">{m.author_role.toUpperCase()}</span></div>
                    <div className="tech-label">{new Date(m.created_at).toLocaleString()}</div>
                  </div>
                  {m.text && <div className="text-sm text-[var(--gc-navy)] whitespace-pre-wrap">{m.text}</div>}
                  {(m.asset_ids || []).length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1.5" data-testid={`message-attachments-${m.id}`}>
                      {m.asset_ids.map((fid) => <MessageAttachment key={fid} fileId={fid} />)}
                    </div>
                  )}
                </div>
              ))}
              {(!job.messages || job.messages.length === 0) && <div className="text-[var(--gc-text-muted)] text-sm">No messages yet.</div>}
            </div>
            {job.phase !== "Completed" && (
              <div className="mt-4 space-y-2">
                <AssetUploader value={msgAssets} onChange={setMsgAssets} label="Attach files (optional)" testid="message-assets" />
                <textarea value={msgText} onChange={(e) => setMsgText(e.target.value)} placeholder="Write a message…" rows={3}
                  className="gc-input" data-testid="message-input" />
                <button onClick={sendMessage} disabled={!msgText.trim()}
                  className="btn-primary inline-flex items-center gap-2 px-4 py-2 text-sm disabled:opacity-50"
                  data-testid="send-message-btn">
                  <PaperPlaneRight size={16} weight="bold"/> Send
                </button>
              </div>
            )}
          </section>
        );

      case "Shipping/Go Live":
      case "Completed":
        return job.shipping ? (
          <section className="pt-6" data-testid="phase-section-shipping">
            <div className="chip chip-orange mb-4">// {job.shipping.tracking_url ? "shipping" : "go live"}</div>
            <div className="space-y-3 text-sm">
              {job.shipping.tracking_url && (
                <a href={job.shipping.tracking_url} target="_blank" rel="noreferrer" className="text-[var(--gc-orange)] font-semibold link-underline inline-flex items-center gap-2" data-testid="tracking-link">
                  <LinkIcon size={14} weight="bold"/> Tracking → {job.shipping.tracking_url}
                </a>
              )}
              {job.shipping.live_url && (
                <a href={job.shipping.live_url} target="_blank" rel="noreferrer" className="text-[var(--gc-orange)] font-semibold link-underline inline-flex items-center gap-2" data-testid="live-link">
                  <LinkIcon size={14} weight="bold"/> Live at → {job.shipping.live_url}
                </a>
              )}
              {job.shipping.notes && <div className="text-[var(--gc-navy)]">{job.shipping.notes}</div>}
              <div className="tech-label">Auto-completes on {new Date(job.shipping.auto_complete_at).toLocaleDateString()}</div>
            </div>
          </section>
        ) : (
          <section className="pt-6"><div className="text-sm text-[var(--gc-text-muted)]">Shipping / go-live details haven&apos;t been posted yet.</div></section>
        );

      default:
        return null;
    }
  };

  return (
    <Layout>
      <div className="max-w-[1400px] mx-auto px-8 py-10 space-y-8">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="chip mb-3">Job · {job.job_number}</div>
            <h1 className="serif text-4xl md:text-5xl">{job.project_name}</h1>
            <div className="text-[var(--gc-text-muted)] mt-2 text-lg">{job.project_type}</div>
          </div>
          <div>
            <div className="tech-label">Current Phase</div>
            <div className="serif text-3xl text-[var(--gc-orange)] mt-1">{job.phase}</div>
          </div>
        </div>

        <div className="pt-4">
          <JobTimeline
            currentPhase={job.phase}
            selectedPhase={activePhase}
            onSelectPhase={setSelectedPhase}
            hadRevisions={hadRevisions}
            hadRejection={hadRejection}
            reachedPhases={{
              "Final Proof Sign-Off": !!job.final_proof,
              "Production": !!job.approval && job.approval?.status !== "not_approved",
              "Shipping/Go Live": !!job.shipping,
            }}
          />
        </div>

        {(job.activity_log || []).length > 0 && (
          <details className="border border-[var(--gc-border)] p-4 bg-white" data-testid="customer-activity-log">
            <summary className="cursor-pointer tech-label text-[var(--gc-orange)] font-bold">
              Project activity ({job.activity_log.length})
            </summary>
            <ol className="mt-3 space-y-2 text-sm">
              {job.activity_log.slice().reverse().map((e) => (
                <li key={e.id} className="border-l-4 border-[var(--gc-navy)] pl-3" data-testid={`customer-activity-${e.id}`}>
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="font-semibold text-[var(--gc-navy)]">
                      {e.kind === "closed" ? "Project closed" : e.kind === "reopened" ? `Project reopened → ${e.to_phase}` : e.kind}
                    </div>
                    <div className="tech-label">{new Date(e.at).toLocaleString()}</div>
                  </div>
                  <div className="text-xs text-[var(--gc-text-muted)] font-mono-tech">by {e.by_name}</div>
                  {e.note && <div className="mt-1 whitespace-pre-wrap text-[var(--gc-navy)]">“{e.note}”</div>}
                </li>
              ))}
            </ol>
          </details>
        )}

        <div className="hairline" />

        {renderPhase()}
      </div>
    </Layout>
  );
}
